import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { OrdenableLista } from '@/components/OrdenableLista';
import { PaginaEditor } from '@/components/PaginaEditor';
import { SubirVarios, type ArchivoSubidoCola } from '@/components/SubirVarios';
import { cn } from '@/lib/utils';

import { ArchivoUnico, type ValorArchivo } from '../comun/ArchivoUnico';
import { CasosLigados, NotasContenido } from '../comun/Paneles';
import { SelectorEspecialidad } from '../comun/SelectorEspecialidad';
import { useManual, useManualMutaciones, type Manual, type PaginaManual } from './queries';

type Mut = ReturnType<typeof useManualMutaciones>;

const ESTATUS: Record<string, string> = {
  updated: 'Actualizada',
  outdated: 'Desactualizada',
  notes: 'Con notas',
};

function AgregarPaginas({
  m,
  mut,
  abierto,
  onCerrar,
}: {
  m: Manual;
  mut: Mut;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const [listos, setListos] = useState<ArchivoSubidoCola[]>([]);
  const [pendientes, setPendientes] = useState(0);
  const [despues, setDespues] = useState<number | null>(null);
  const [especialidad, setEspecialidad] = useState<number | null>(null);
  return (
    <Modal abierto={abierto} onCerrar={onCerrar} titulo="Agregar páginas" ancho="max-w-3xl">
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etiqueta="Dónde">
            <select
              className="input-vidrio"
              value={despues ?? ''}
              onChange={(e) => setDespues(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Al final</option>
              {m.pages.map((p, i) => (
                <option key={p.id} value={p.id}>
                  Después de la {i + 1} ({p.name})
                </option>
              ))}
            </select>
          </Campo>
          <Campo etiqueta="Especialidad (para todas)">
            <SelectorEspecialidad valor={especialidad} onCambio={setEspecialidad} />
          </Campo>
        </div>
        <SubirVarios
          destino="manual"
          ownerId={m.legacyId}
          maxNombre={100}
          onCambio={(l, n) => {
            setListos(l);
            setPendientes(n);
          }}
        />
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-cs btn-primario"
            disabled={
              listos.length === 0 ||
              pendientes > 0 ||
              listos.some((a) => !a.nombre) ||
              mut.agregar.isPending
            }
            onClick={() =>
              mut.agregar.mutate(
                {
                  afterId: despues,
                  specialtyId: especialidad,
                  files: listos.map((a) => ({ filePath: a.path, name: a.nombre })),
                },
                {
                  onSuccess: () => {
                    toast.success(`${listos.length} página(s) agregada(s).`);
                    onCerrar();
                  },
                },
              )
            }
          >
            Agregar {listos.length}
          </button>
        </div>
      </div>
    </Modal>
  );
}

const PESTANAS_PAGINA = [
  ['datos', 'Datos'],
  ['casos', 'Casos'],
  ['notas', 'Notas'],
] as const;

function ModalPagina({
  m,
  pagina,
  mut,
  onCerrar,
}: {
  m: Manual;
  pagina: PaginaManual;
  mut: Mut;
  onCerrar: () => void;
}) {
  const editable = useCan('contenido', 'editar');
  const [pestana, setPestana] = useState<(typeof PESTANAS_PAGINA)[number][0]>('datos');
  const [f, setF] = useState<{
    name: string;
    status: string;
    isTitle: boolean;
    isEnglish: boolean;
    specialtyId: number | null;
    archivo: ValorArchivo | null;
  }>({
    name: pagina.name,
    status: pagina.status ?? 'updated',
    isTitle: pagina.isTitle,
    isEnglish: pagina.isEnglish,
    specialtyId: pagina.specialtyId,
    archivo: { path: pagina.filePath, url: pagina.fileUrl },
  });
  const [borrando, setBorrando] = useState(false);
  const poner = (c: Partial<typeof f>) => setF((v) => ({ ...v, ...c }));
  return (
    <Modal abierto onCerrar={onCerrar} titulo={pagina.name} ancho="max-w-2xl">
      <div className="space-y-4">
        <div role="tablist" aria-label="Secciones" className="flex gap-2">
          {PESTANAS_PAGINA.map(([k, t]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={pestana === k}
              className={cn('btn-cs', pestana === k ? 'btn-primario' : 'btn-vidrio')}
              onClick={() => setPestana(k)}
            >
              {t}
            </button>
          ))}
        </div>
        {pestana === 'datos' && (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              mut.editar.mutate(
                {
                  childId: pagina.id,
                  body: {
                    name: f.name.trim(),
                    status: f.status as 'updated' | 'outdated' | 'notes',
                    isTitle: f.isTitle,
                    isEnglish: f.isEnglish,
                    specialtyId: f.specialtyId,
                    ...(f.archivo &&
                      f.archivo.path !== pagina.filePath && { filePath: f.archivo.path }),
                  },
                },
                {
                  onSuccess: () => {
                    toast.success('Página guardada.');
                    onCerrar();
                  },
                },
              );
            }}
          >
            <fieldset disabled={!editable} className="space-y-4">
              <Campo etiqueta="Nombre">
                <input
                  className="input-vidrio"
                  value={f.name}
                  required
                  maxLength={100}
                  onChange={(e) => poner({ name: e.target.value })}
                />
              </Campo>
              <div className="grid gap-3 sm:grid-cols-2">
                <Campo etiqueta="Estatus">
                  <select
                    className="input-vidrio"
                    value={f.status}
                    onChange={(e) => poner({ status: e.target.value })}
                  >
                    {Object.entries(ESTATUS).map(([k, t]) => (
                      <option key={k} value={k}>
                        {t}
                      </option>
                    ))}
                  </select>
                </Campo>
                <Campo etiqueta="Especialidad">
                  <SelectorEspecialidad
                    valor={f.specialtyId}
                    deshabilitado={!editable}
                    onCambio={(specialtyId) => poner({ specialtyId })}
                  />
                </Campo>
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[var(--color-azul-600)]"
                    checked={f.isTitle}
                    onChange={(e) => poner({ isTitle: e.target.checked })}
                  />
                  Título destacado
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[var(--color-azul-600)]"
                    checked={f.isEnglish}
                    onChange={(e) => poner({ isEnglish: e.target.checked })}
                  />
                  En inglés
                </label>
              </div>
              <ArchivoUnico
                destino="manual"
                ownerId={m.legacyId}
                tipos={['image']}
                valor={f.archivo}
                deshabilitado={!editable}
                etiqueta="Imagen"
                onCambio={(archivo) => poner({ archivo })}
              />
            </fieldset>
            {editable && (
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="btn-cs btn-vidrio mr-auto text-rojo-texto"
                  onClick={() => setBorrando(true)}
                >
                  <Trash2 size={16} aria-hidden /> Borrar página
                </button>
                <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-cs btn-primario"
                  disabled={mut.editar.isPending}
                >
                  Guardar
                </button>
              </div>
            )}
          </form>
        )}
        {pestana === 'casos' && (
          <CasosLigados destino={{ contentType: 'manual_page', contentId: pagina.id }} />
        )}
        {pestana === 'notas' && (
          <>
            <p className="text-xs text-texto-3">
              Al agregar una nota la página queda con estatus «Con notas».
            </p>
            <NotasContenido destino={{ contentType: 'manual_page', contentId: pagina.id }} />
          </>
        )}
      </div>
      <ConfirmarAccion
        abierto={borrando}
        onCerrar={() => setBorrando(false)}
        titulo={`Borrar «${pagina.name}»`}
        descripcion="Se borra la página con sus notas y casos ligados. Si una clase la usa en Ultra Resumen, no se puede borrar."
        textoBoton="Borrar"
        peligro
        ocupado={mut.borrar.isPending}
        onConfirmar={() =>
          mut.borrar.mutate(pagina.id, {
            onSuccess: () => {
              toast.success('Página borrada.');
              onCerrar();
            },
          })
        }
      />
    </Modal>
  );
}

function Editor({ m }: { m: Manual }) {
  const editable = useCan('contenido', 'editar');
  const mut = useManualMutaciones(m.id);
  const [agregando, setAgregando] = useState(false);
  const [abierta, setAbierta] = useState<number | null>(null);
  const pagina = m.pages.find((p) => p.id === abierta) ?? null;
  return (
    <PaginaEditor
      volver={{ a: '/contenido/manuales', texto: 'Manuales' }}
      titulo={m.name}
      descripcion={`Manual #${m.id} · ${m.edition === 'advance' ? 'Advance' : 'Básico'} · ${m.pages.length} páginas`}
      acciones={
        editable && (
          <button type="button" className="btn-cs btn-primario" onClick={() => setAgregando(true)}>
            <Plus size={16} aria-hidden /> Agregar páginas
          </button>
        )
      }
    >
      {m.pages.length === 0 ? (
        <Vacio titulo="Sin páginas" />
      ) : (
        <div className="panel-vidrio p-0">
          <div className="grid grid-cols-[2rem_4rem_3rem_1fr_10rem_8rem_6rem] gap-2 border-b border-[var(--separador)] px-3 py-2 text-xs font-bold text-texto-3 max-lg:hidden">
            <span />
            <span>Imagen</span>
            <span>#</span>
            <span>Nombre</span>
            <span>Especialidad</span>
            <span>Estatus</span>
            <span>Casos · notas</span>
          </div>
          <OrdenableLista
            items={m.pages}
            idDe={(p) => p.id}
            deshabilitado={!editable || mut.ordenar.isPending}
            onOrdenar={(ids) => mut.ordenar.mutate(ids)}
          >
            {(p, asa, i) => (
              <div className="grid grid-cols-[2rem_4rem_3rem_1fr] items-center gap-2 border-b border-[var(--separador)] px-3 py-1.5 text-sm lg:grid-cols-[2rem_4rem_3rem_1fr_10rem_8rem_6rem]">
                <span>{asa}</span>
                {p.fileUrl ? (
                  <img
                    src={p.fileUrl}
                    alt=""
                    loading="lazy"
                    className="h-10 w-14 rounded object-cover"
                  />
                ) : (
                  <span />
                )}
                <span className="tabular text-texto-3">{i + 1}</span>
                <button
                  type="button"
                  className="truncate text-left font-semibold hover:underline"
                  onClick={() => setAbierta(p.id)}
                >
                  {p.name}
                  {p.isTitle && <span className="insignia ml-2">Título</span>}
                  {p.isEnglish && <span className="insignia ml-2">Inglés</span>}
                </button>
                <span className="truncate text-texto-2 max-lg:hidden">
                  {p.specialtyName ?? '—'}
                </span>
                <span className="max-lg:hidden">{ESTATUS[p.status ?? ''] ?? '—'}</span>
                <span className="tabular text-texto-2 max-lg:hidden">
                  {p.casesCount} · {p.notesCount}
                </span>
              </div>
            )}
          </OrdenableLista>
        </div>
      )}
      <AgregarPaginas
        key={String(agregando)}
        m={m}
        mut={mut}
        abierto={agregando}
        onCerrar={() => setAgregando(false)}
      />
      {pagina && (
        <ModalPagina
          key={pagina.id}
          m={m}
          pagina={pagina}
          mut={mut}
          onCerrar={() => setAbierta(null)}
        />
      )}
    </PaginaEditor>
  );
}

export function ManualPage() {
  const id = Number(useParams().id);
  const q = useManual(id);
  return (
    <PermissionGate seccion="contenido">
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data && <Editor m={q.data} />}
    </PermissionGate>
  );
}
